/* global HTMLVideoElement */
import React, { CSSProperties, useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { EventHandlerMap } from 'app/utils/EventMapEmitter';
import { bindDomEvents, unbindDomEvents } from 'app/utils/DomEvents';
import { notify } from 'app/services/errorReporter';
import { Playback } from 'app/services/rtc/Playback';
import { createSelector } from 'app/utils/reselect-legacy';

export enum MediaType {
  Audio = 'audio',
  Video = 'video',
}

export type MediaElementSource = MediaStream | string;

type ElementType<T extends MediaType> =
  T extends MediaType.Audio ? HTMLAudioElement :
  T extends MediaType.Video ? HTMLVideoElement :
  never;

interface IProps<T extends MediaType> {
  readonly type: MediaType;
  readonly src: MediaElementSource;
  readonly playback: Playback;
  readonly disableAudio?: boolean;
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly preload?: 'none' | 'metadata' | 'auto';
  readonly getRef?: (element: ElementType<T> | null) => void;

  // Video-only props:
  readonly playsInline?: T extends MediaType.Video ? boolean : never;
}

function makeChangeCounter<T>(init: T) {
  let counter = 0;
  let prev = init;

  return (value: T) => {
    if (value !== prev) {
      ++counter;
      prev = value;
    }

    return counter;
  };
}

/**
 * Either a `<video>` or `<audio>` element (depending on the `type`
 * prop). Requires a `Playback` implementation provided in the
 * `playback` prop to control playback.
 */
function MediaElement<T extends MediaType>({
  playback,
  src,
  playsInline,
  className,
  style,
  preload,
  disableAudio,
  type,
  getRef,
}: IProps<T>) {
  const [getSourceKey] = useState(() => (
    createSelector(
      // Update the source key to restart playback when:
      // - track configuration of media stream change
      // - reference to the source changes
      //
      // We need the first one because MediaStreams are mutable
      // objects. We need the second for strings but also because
      // Chrome is reusing ids for media streams and tracks on peer
      // connections, even if the connection was recreated.
      (mediaSrc: MediaElementSource) => {
        if (typeof mediaSrc === 'string') {
          return mediaSrc;
        } else {
          return mediaSrc.getTracks().map(({ id }) => id).join(':');
        }
      },
      makeChangeCounter<MediaElementSource>(''),
      (mediaSrc: string, counter: number) => `${mediaSrc}-${counter}`,
    )
   ));

  const element = useRef<ElementType<T> | null>(null);

  const trigger = useCallback((name: 'onCanPlay' | 'onPlay' | 'onPause' | 'onEnded' | 'onWaiting') => {
    const method = playback[name];
    if (method) {
      method();
    }
  }, [playback]);

  const triggerProgressEvent = useCallback(
    (name: 'onDurationChange' | 'onLoadedMetaData' | 'onSeeked' | 'onSeeking' | 'onTimeUpdate') => {
      const callback = playback[name];

      if (!element.current || !callback) {
        return;
      }

      const { currentTime, duration } = element.current;
      callback({ currentTime, duration });
    },
  [playback]);

  const triggerErrorEvent = useCallback(() => {
    if (element.current?.error && playback.onError) {
      playback.onError(element.current.error);
    }
  }, [playback]);

  const play = useCallback(async () => {
    const { onVideoBlocked, onSoundBlocked } = playback;
    const onBlocked = type === MediaType.Audio ? onSoundBlocked : onVideoBlocked;

    try {
      await element.current?.play();
    } catch (error) {
      if (error.name === 'NotAllowedError' && onBlocked) {
        onBlocked(error);
      } else if (error.name === 'AbortError') {
        // Don't report abort errors like "The play() request was
        // interrupted by a call to pause()". They happen if the
        // component is unmounted during awaiting on the promise
        // returned from `element.play()`.
      } else {
        notify(error, { name: 'MediaElement.play', type });
      }
    }
  }, [type, playback]);

  const elementEventMap = useMemo<EventHandlerMap<HTMLMediaElementEventMap>>(() => ({
    canplay:        () => trigger('onCanPlay'),
    ended:          () => trigger('onEnded'),
    pause:          () => trigger('onPause'),
    play:           () => trigger('onPlay'),
    waiting:        () => trigger('onWaiting'),
    durationchange: () => triggerProgressEvent('onDurationChange'),
    loadedmetadata: () => triggerProgressEvent('onLoadedMetaData'),
    seeked:         () => triggerProgressEvent('onSeeked'),
    seeking:        () => triggerProgressEvent('onSeeking'),
    timeupdate:     () => triggerProgressEvent('onTimeUpdate'),
    error:          () => triggerErrorEvent(),
  }), [trigger, triggerProgressEvent, triggerErrorEvent]);

  const playbackOutlet = useMemo(() => ({
    play: async () => { if (element.current) return await play(); },
    pause: () => { element.current?.pause(); },
    mute: (muted: boolean) => {
      if (element.current) {
        element.current.muted = disableAudio || muted;
      }
    },
    seek: (time: number) => { if (element.current) element.current.currentTime = time; },
  }), [play, disableAudio]);

  const setElement = useCallback((node: ElementType<T> | null) => {
    if (element.current) {
      unbindDomEvents(element.current, elementEventMap);
      // The media is paused by the browser when the media element is removed from the DOM or when the source
      // is unset, but not manually pausing the media can result in freezing on certain Android devices using Chrome.
      playbackOutlet.pause();
      element.current.srcObject = null;
    }

    element.current = node;

    if (element.current) {
      bindDomEvents(element.current, elementEventMap);

      if (typeof src === 'string') {
        element.current.src = src;
      } else {
        element.current.srcObject = src;
      }
    }
  }, [src, elementEventMap, playbackOutlet]);

  const combinedRef = useCallback((node: ElementType<T> | null) => {
    setElement(node);
    getRef?.(node);
  }, [setElement, getRef]);

  useEffect(() => {
    playback.addPlaybackOutlet(playbackOutlet);
    return () => {
      playback.removePlaybackOutlet(playbackOutlet);
    };
  }, [playback, playbackOutlet]);

  return React.createElement(type, {
    ref: combinedRef,
    key: getSourceKey(src),
    playsInline,
    className,
    style,
    preload,
    muted: disableAudio,
  });
}

// Workaround for react types for `memo` not working well with components that use generics
const MemoizedMediaElement = React.memo(MediaElement) as unknown as typeof MediaElement;
export default MemoizedMediaElement;
